// Ubicación en el repo: src/lib/professionals.js
// Profesionales — spec-prototipo.md 1.5 / DECISIONS.md #16. El alta real
// (crear el usuario de auth) y el reenvío de acceso pasan por Edge
// Functions (service_role) — todo lo demás (listar, editar la ficha,
// asignar sucursales, archivar) es CRUD normal protegido por RLS.
import { supabase } from './supabaseClient';

// 'Médico ORL'/'Médico OFT' se sacaron del sistema (DECISIONS.md #57):
// eran roles informativos que nunca se usaron, ningún profesional real los
// tenía asignado. Si en el futuro se necesita un rol nuevo, se agrega acá
// y en el check constraint de la base con una migración chica — no hay ni
// se planea una pantalla de "crear rol" en Configuración.
export const ROLES = ['TM ORL', 'Fonoaudiólogo(a)', 'TM OFT', 'Recepcionista', 'Vendedor(a)'];

// Mismos arrays que el prototipo (const ORL_ROLES / OFT_ROLES), confirmados
// tal cual en prototipo-actual.html.html línea ~2663.
export const ORL_ROLES = ['TM ORL', 'Fonoaudiólogo(a)'];
export const OFT_ROLES = ['TM OFT'];

// Lista de profesionales activos + sus sucursales asignadas, para el combo
// "quién atiende" del modal de cita (mismo patrón de dos consultas que
// listAllProfessionalsForAdmin, sin necesitar ser Admin: professional_branches_select es `using (true)`).
export async function listActiveProfessionals() {
  const [profResult, branchResult] = await Promise.all([
    supabase
      .from('professionals')
      .select('id, nombre, apellido, role')
      .is('deleted_at', null)
      .order('nombre', { ascending: true }),
    supabase.from('professional_branches').select('professional_id, branch_id'),
  ]);
  if (profResult.error) throw profResult.error;
  if (branchResult.error) throw branchResult.error;

  const branchesByProf = new Map();
  for (const row of branchResult.data ?? []) {
    if (!branchesByProf.has(row.professional_id)) branchesByProf.set(row.professional_id, []);
    branchesByProf.get(row.professional_id).push(row.branch_id);
  }
  return (profResult.data ?? []).map((p) => ({ ...p, branch_ids: branchesByProf.get(p.id) ?? [] }));
}

// Filtro por módulo (rol) + sucursal activa, igual que el prototipo: un
// profesional sin ninguna sucursal asignada se considera con acceso a
// todas (mismo criterio que has_branch_access en el backend), así que
// aparece en el combo de cualquier sucursal.
export function professionalsForModuleAndBranch(professionals, module, branchId) {
  const allowedRoles = module === 'ORL' ? ORL_ROLES : OFT_ROLES;
  return (professionals ?? []).filter(
    (p) => allowedRoles.includes(p.role) && (!p.branch_ids?.length || p.branch_ids.includes(branchId))
  );
}

// Para la pantalla de administración (solo Admin/bootstrap llega acá, ver
// RLS `professionals_select`): trae todos (incl. archivados) + sus
// sucursales asignadas, en dos consultas simples en vez de una vista nueva.
export async function listAllProfessionalsForAdmin() {
  const [profResult, branchResult] = await Promise.all([
    supabase
      .from('professionals')
      .select('id, nombre, segundo_nombre, apellido, segundo_apellido, rut, role, email, phone, is_admin, mfa_required, auth_user_id, signature_path, deleted_at, created_at')
      .order('nombre', { ascending: true }),
    supabase.from('professional_branches').select('professional_id, branch_id'),
  ]);
  if (profResult.error) throw profResult.error;
  if (branchResult.error) throw branchResult.error;

  const branchesByProf = new Map();
  for (const row of branchResult.data ?? []) {
    if (!branchesByProf.has(row.professional_id)) branchesByProf.set(row.professional_id, []);
    branchesByProf.get(row.professional_id).push(row.branch_id);
  }
  return (profResult.data ?? []).map((p) => ({ ...p, branch_ids: branchesByProf.get(p.id) ?? [] }));
}

export async function sanitizeRut(rut) {
  if (!rut?.trim()) return null;
  const { data, error } = await supabase.rpc('sanitize_rut', { p_rut: rut });
  if (error) throw error;
  return data;
}

export async function findProfessionalByRut(sanitizedRut, excludeId) {
  if (!sanitizedRut) return null;
  let query = supabase
    .from('professionals')
    .select('id, nombre, apellido')
    .eq('rut', sanitizedRut)
    .is('deleted_at', null)
    .limit(1);
  if (excludeId) query = query.neq('id', excludeId);
  const { data, error } = await query;
  if (error) throw error;
  return data?.[0] ?? null;
}

// Alta de profesional nuevo — pasa por la Edge Function invite-professional
// (DECISIONS.md #16): crea el usuario de auth (correo de invitación vía
// Resend/SMTP) y la ficha en un solo paso, con permisos verificados del
// lado del servidor.
export async function inviteProfessional(values, branchIds) {
  const redirectTo = `${window.location.origin}/definir-password`;
  const { data, error } = await supabase.functions.invoke('invite-professional', {
    body: { ...values, branchIds, redirectTo },
  });
  if (error) {
    // supabase-js no siempre expone el body de error en `error.message` —
    // se intenta leer el JSON de la respuesta para dar un mensaje útil.
    const detail = await error.context?.json?.().catch(() => null);
    throw new Error(detail?.error || error.message || 'No se pudo crear el profesional.');
  }
  if (data?.error) throw new Error(data.error);
  return data;
}

// Editar la ficha de un profesional existente (Admin: todos los campos;
// auto-edición sin ser Admin: el trigger `trg_professionals_self_update_guard`
// en el backend solo deja pasar email/phone/signature_path, y rechaza el
// resto con una excepción — acá no se duplica esa validación, se confía en
// el mensaje de error que devuelva si alguien intenta más de la cuenta).
export async function updateProfessional(id, values, branchIds) {
  const { error } = await supabase.from('professionals').update(values).eq('id', id);
  if (error) throw error;
  if (branchIds !== undefined) await saveProfessionalBranches(id, branchIds);
}

async function saveProfessionalBranches(professionalId, branchIds) {
  const { error: deleteError } = await supabase
    .from('professional_branches')
    .delete()
    .eq('professional_id', professionalId);
  if (deleteError) throw deleteError;

  if (!branchIds?.length) return;
  const { error: insertError } = await supabase
    .from('professional_branches')
    .insert(branchIds.map((branch_id) => ({ professional_id: professionalId, branch_id })));
  if (insertError) throw insertError;
}

export async function softDeleteProfessional(id, actorId) {
  const { error } = await supabase
    .from('professionals')
    .update({ deleted_at: new Date().toISOString(), deleted_by: actorId })
    .eq('id', id);
  if (error) throw error;
}

export async function restoreProfessional(id) {
  const { error } = await supabase
    .from('professionals')
    .update({ deleted_at: null, deleted_by: null })
    .eq('id', id);
  if (error) throw error;
}

// Botón único "Reenviar acceso" (DECISIONS.md #16) — pasa por la Edge
// Function manage-professional-credentials: genera un link de
// restablecimiento y lo manda por correo vía Resend, sirve tanto para un
// profesional que nunca activó su cuenta como para uno que ya la usa.
export async function resendAccess(professionalId) {
  const redirectTo = `${window.location.origin}/definir-password`;
  const { data, error } = await supabase.functions.invoke('manage-professional-credentials', {
    body: { professionalId, redirectTo },
  });
  if (error) {
    const detail = await error.context?.json?.().catch(() => null);
    throw new Error(detail?.error || error.message || 'No se pudo reenviar el acceso.');
  }
  if (data?.error) throw new Error(data.error);
  return data;
}

// Le saca el/los factor(es) 2FA a un profesional (DECISIONS.md #27) — para
// cuando pierde su teléfono. En su próximo login le va a volver a pedir
// enrolar, si su rol lo sigue exigiendo. No sirve para el único Admin que
// pierde su propio segundo factor — para eso existe scripts/reset-mfa.mjs.
export async function resetProfessionalMfa(professionalId) {
  const { data, error } = await supabase.functions.invoke('reset-professional-mfa', {
    body: { professionalId },
  });
  if (error) {
    const detail = await error.context?.json?.().catch(() => null);
    throw new Error(detail?.error || error.message || 'No se pudo resetear el 2FA.');
  }
  if (data?.error) throw new Error(data.error);
  return data;
}
